/**
 * repaymentsDriver.ts — WHO the repayments page names against a contract.
 *
 * THE `drivers → profiles` EMBED IS NOT ONE ROW. `profiles.driver_id` carries a partial unique
 * index — `UNIQUE (driver_id) WHERE admin_id IS NULL` — precisely so an admin may point their own
 * profile at a driver id to test the driver app. Unwrapping that embed by position is a coin toss,
 * and on this page it landed wrong in production: contract 318's driver (286) carries the owner's
 * ADMIN profile (226, Joseph Paddy) beside the real one (494, Peter Azaglo, created 2026-08-17),
 * and the Collections table printed the admin in its Driver column.
 *
 * THE SERVER ALREADY DOES THIS ON THE OTHER TAB. `get_all_contract_transactions`, behind the
 * Transactions tab, joins `profiles p ON p.driver_id = d.id AND p.admin_id IS NULL` at both of its
 * sites, so it always named Peter. These helpers restate that same exclusion for the two surfaces
 * that read the embed in the browser, via `driverProfileOf` — one page, one person.
 *
 * PURE, AND REACT-FREE, so a node-env test can load it.
 */

import { driverProfileOf, driverProfileName } from '@/lib/driverProfile';

/** What a Collections row shows about the person: their name, their phone, their photo. */
export interface OutstandingDriverIdentity {
    driver_name: string;
    phone_number: string | null;
    driver_photo: string | null;
}

/**
 * The driver behind a Collections row — all three fields off the SAME row.
 *
 * Taking them one embed at a time is how a row ends up half one person and half another: the
 * admin's name over the driver's phone number. The pick happens once, here.
 *
 * 'Unknown' WHERE THE DRIVER HAS NO ROW OF THEIR OWN, which is what the server says about them
 * too — never the admin's name as a stand-in.
 */
export function outstandingDriverIdentity(drivers: unknown): OutstandingDriverIdentity {
    const profile = driverProfileOf(
        (drivers as { profiles?: unknown } | null | undefined)?.profiles as never,
    ) as {
        first_name?: string | null;
        middle_name?: string | null;
        last_name?: string | null;
        phone?: string | null;
        photo?: string | null;
    } | null;

    return {
        driver_name: profile
            ? [profile.first_name, profile.middle_name, profile.last_name]
                  .filter(Boolean)
                  .join(' ')
                  .trim() || 'Unknown'
            : 'Unknown',
        phone_number: profile?.phone || null,
        driver_photo: profile?.photo || null,
    };
}

/**
 * The name a contract is listed and searched under in the filter sheet — and written onto the
 * page's filter badge when one is picked.
 *
 * The badge is the same sentence the Driver column makes, so it is picked the same way: a sheet
 * that offered "Joseph Paddy" for contract 318 would put the admin's name back on the page the
 * moment an admin filtered by it.
 */
export function filterContractDriverName(
    contract: { drivers?: unknown } | null | undefined,
): string {
    if (!contract) return 'Unknown';
    const drivers = contract.drivers as { profiles?: unknown } | null | undefined;
    return driverProfileName(drivers?.profiles as never) ?? 'Unknown';
}
